import { useEffect, Fragment, useState, useMemo, useCallback } from 'react';
import QrCard from '../QrCard';
import { useLocation, useNavigate } from 'react-router-dom';
import Collapse from 'react-bootstrap/Collapse';
import QrDisplayMenu from '../QrDisplayMenu';
import QrDragNDrop from '../QrDragNDrop';
import { useAuth } from '../../../utils/Auth';
import { useCategoryOrder } from '../../../utils/hooks/useCategoryOrder';
import { useLocalStorage } from '../../../utils/hooks/useLocalStorage';
import { AnimatePresence, motion } from 'motion/react';
import { transition, variants } from './variants';
import styles from './QrReturnByCategory.module.css';

export default function QrReturnByCategory(props) {
    const { 
        searchReturnArr,
        layout,
        setFilters,
        searchValue,
        linkedQRs
    } = props;

    const { activeOrgId, orgObj } = useAuth();
    const categories = useMemo(() => orgObj?.categories || [], [orgObj?.categories]);
    const [ categoryStates, setCategoryStates ] = useState(() => {
        const initialCategories = [ ...categories, { id: 'Uncategorized', name: 'Uncategorized' }];
        return initialCategories.reduce((acc, cur) => {
            acc[cur.id] = true;
            return acc;
        },{});
    });
    const [ storedQrId, setStoredQrId ] = useLocalStorage('qr_id');

    // category stuff
    const [ editOrder, setEditOrder ] = useState(false);
    const [ categoryOrder, setCategoryOrder ] = useCategoryOrder(categories, activeOrgId);
    const validCategoryIds = useMemo(() => new Set(categoryOrder.map(c => c.id)), [categoryOrder]);

    const location = useLocation();
    const navigate = useNavigate();
    // ------ Scroll Edit -> Back ------
    const clearState = () => {
        setStoredQrId(null);
        navigate(location.pathname, { replace: true });
    };
    const isQrWithinWindow = (y) => {
        const wHeight = window.innerHeight || document.documentElement.clientHeight;
        return (y >= 0 && y >= wHeight);
    };

    useEffect(() => {
        if (!storedQrId || !searchReturnArr?.find(qr => qr.id === storedQrId)) {
            return;
        }

        const qr = document.getElementById(storedQrId);
        if (qr) {
            const position = qr.offsetTop;
            if (isQrWithinWindow(position)) window.scrollTo({ top: position, behavior: 'smooth' });
            clearState();
        }
    },[storedQrId, searchReturnArr, clearState, isQrWithinWindow]);
    // ---------- End Scroll -----------

    useEffect(() => {
        setCategoryStates(prev => {
            const newStates = { ...prev };
            categories.forEach(cat => {
                if (!(cat.id in newStates)) {
                    newStates[cat.id] = true;
                }
            });
            return newStates;
        });
    }, [categories]);

    const handleCollapse = useCallback((e) => {
        const id = e.target.id;
        setCategoryStates(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    },[]);

    return (
        <>
        <AnimatePresence mode='wait' initial={false}>
            {!editOrder ? (
                <motion.div 
                    key='qrs'
                    className={styles.qrs}
                    transition={transition}
                    variants={variants}
                    initial='initial'
                    animate='animate'
                    exit='initial'
                >
                    {searchValue ?
                        <>
                        <div className={styles.resultsTitle}>Results for "<span>{searchValue}</span>"...</div>
                        <div className={`${styles.qrsSection} ${styles.open}`}>
                            {searchReturnArr.map((qr, index) => (
                                <Fragment key={qr.id}>
                                    <QrCard QR={qr} layout={layout} childQRs={linkedQRs?.filter(child => child?.data?.linked_qr === qr?.id)} />    
                                </Fragment>
                            ))}
                        </div> 
                        </>
                    : 
                        categoryOrder?.map((cat,i) => (
                            <Fragment key={cat.id}>
                                <button className={`${styles.sectionHeader} ${categoryStates[cat.id] ? '' : styles.close}`} id={cat.id} onClick={handleCollapse}>
                                    {/* <i className='far fa-caret-up me-2' /> */}
                                    <span>{cat.name}</span>
                                    {/* <i className='far fa-caret-up ms-2' /> */}
                                    <i className={`far fa-${categoryStates[cat.id] ? 'minus' : 'plus'}`} />
                                </button>
                                <Collapse in={categoryStates[cat.id]}>
                                    <div style={{width: '100%'}}>
                                        <div className={styles.qrsSection}>
                                            {searchReturnArr.filter(qr => qr.data.category === cat.id).length > 0 ?
                                                <>
                                                {searchReturnArr.filter(qr => qr.data.category === cat.id).map((qr, index) => (
                                                    <QrCard key={qr.id} QR={qr} layout={layout} childQRs={linkedQRs?.filter(child => child?.data?.linked_qr === qr?.id)} />    
                                                ))}
                                                </>
                                            : (cat.id === 'Uncategorized') ? 
                                                <>
                                                {searchReturnArr.filter(qr => !qr.data.category || !validCategoryIds.has(qr.data.category)).map((qr, index) => (
                                                    <QrCard key={qr.id} QR={qr} layout={layout} childQRs={linkedQRs?.filter(child => child?.data?.linked_qr === qr?.id)} />    
                                                ))}
                                                </>
                                                : <div className={styles.noQRs}>0 QRs in this Category</div>
                                            }
                                        </div>
                                    </div>
                                </Collapse>
                            </Fragment>
                        ))
                    }
                </motion.div>
            ) : (
                <motion.div 
                    key='reorder'
                    className={styles.qrsReorder}
                    transition={transition}
                    variants={variants}
                    initial='initial'
                    animate='animate'
                    exit='initial'
                >
                    <QrDragNDrop categoryOrder={categoryOrder} setCategoryOrder={setCategoryOrder} />
                </motion.div>
            )}
        </AnimatePresence>
        {Object.keys(categoryStates).length > 0 ? <QrDisplayMenu categoryOrder={categories.sort((a,b) => a.name.localeCompare(b.name))} editOrder={editOrder} setEditOrder={setEditOrder} categoryStates={categoryStates} setCategoryStates={setCategoryStates} setFilters={setFilters} /> : <></>}
        </>
    );
};